import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ContentIdea } from '@/types/dashboard';
import { logSupabaseError, logError } from '@/utils/sentry';

// Simple in-memory cache to prevent refetching on navigation
const libraryCache: { userId: string | null; data: ContentIdea[]; timestamp: number } = { userId: null, data: [], timestamp: 0 };
const CACHE_DURATION = 60000; // 1 minute cache

/** Invalidate the library cache to force a refetch on next access */
export const invalidateLibraryCache = () => {
  libraryCache.timestamp = 0;
  libraryCache.data = [];
};

export const useLibraryContent = (userId: string | null) => {
  const [isLoading, setIsLoading] = useState(() => {
    // Start with false if we have cached data for this user
    if (libraryCache && libraryCache.userId === userId && libraryCache.data.length > 0) {
      return false;
    }
    return true;
  });
  const [contentIdeas, setContentIdeas] = useState<ContentIdea[]>(() => {
    // Initialize from cache if available
    if (libraryCache && libraryCache.userId === userId) {
      return libraryCache.data;
    }
    return [];
  });
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  
  const { toast } = useToast();

  /**
   * Fetch all content ideas for the user, regardless of source status
   */
  const fetchLibraryContent = useCallback(async (isBackgroundRefresh = false) => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    // Only show loading on initial fetch, not on background refreshes
    if (!isBackgroundRefresh) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Get all content ideas for the user
      const { data, error: fetchError } = await supabase
        .from('content_ideas')
        .select(`
          *,
          source_competitor:scraped_content!content_ideas_source_scraped_content_id_fkey(
            username, 
            likes_count, 
            comments_count, 
            engagement_rate,
            url,
            video_url,
            user_id
          )
        `)
        .eq('user_id', userId)
        .or('is_trashed.is.null,is_trashed.eq.false')
        .not('source_scraped_content_id', 'is', null)
        .order('viral_score', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (fetchError) {
        // Ignore transient network errors during navigation (Load failed)
        const isTransientError = fetchError.message?.includes('Load failed') || fetchError.message?.includes('aborted');
        if (!isTransientError) {
          logSupabaseError('Library: fetch content', fetchError);
          setError('Failed to load library content');
        }
        return;
      }

      // Filter to ensure scraped content also belongs to the user (extra security)
      const filteredData = (data || []).filter((item) => {
        return item.source_competitor && item.source_competitor.user_id === userId;
      });

      // Transform the data to match our ContentIdea interface with explicit user_id filter (defense-in-depth)
      const ideas: ContentIdea[] = filteredData
        .filter((item) => item.user_id === userId) // Explicit security check
        .map((item) => ({
        id: item.id,
        user_id: item.user_id,
        source_scraped_content_id: item.source_scraped_content_id,
        title: item.title,
        description: item.description,
        content: item.content,
        edited_content: item.edited_content,
        platform: item.platform,
        viral_score: item.viral_score,
        hashtags: item.hashtags,
        created_at: item.created_at,
        updated_at: item.updated_at,
        is_favorited: item.is_favorited || false,
        favorited_at: item.favorited_at,
        is_used: item.is_used || false,
        used_at: item.used_at,
        alt_hooks: item.alt_hooks,
        text_overlays: item.text_overlays,
        improvements_applied: item.improvements_applied,
        perplexity_insights: item.perplexity_insights,
        source_competitor: item.source_competitor ? {
          username: item.source_competitor.username,
          likes_count: item.source_competitor.likes_count,
          comments_count: item.source_competitor.comments_count,
          engagement_rate: item.source_competitor.engagement_rate,
          url: item.source_competitor.url,
          video_url: item.source_competitor.video_url
        } : null,
        source: 'scraped' as const
      }));

      setContentIdeas(ideas);
      setLastRefresh(new Date());
      
      // Update cache
      libraryCache.userId = userId;
      libraryCache.data = ideas;
      libraryCache.timestamp = Date.now();
      
    } catch (error) {
      logError('Library: unexpected fetch error', error, { hook: 'useLibraryContent' });
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Copy content to clipboard
   */
  const copyToClipboard = useCallback(async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Skopiowano!",
      });
    } catch (error) {
      console.error('Copy failed:', error);
      toast({
        title: "Copy failed",
        description: "Unable to copy to clipboard",
        variant: "destructive"
      });
    }
  }, [toast]);


  /**
   * Manual refresh
   */
  const refresh = useCallback(() => {
    fetchLibraryContent(true); // Pass true for background refresh
  }, [fetchLibraryContent]);

  // Initial fetch - skip if cache is fresh
  useEffect(() => {
    if (!userId) return;
    
    const isCacheFresh = libraryCache.userId === userId && 
      libraryCache.data.length > 0 && 
      (Date.now() - libraryCache.timestamp) < CACHE_DURATION;
    
    if (!isCacheFresh) {
      fetchLibraryContent();
    }
  }, [userId, fetchLibraryContent]);

  return {
    isLoading,
    contentIdeas,
    error,
    lastRefresh,
    copyToClipboard,
    refresh
  };
};